/**
 * Blitz position persistence utilities
 * Stores and retrieves playback position in localStorage
 */

const STORAGE_PREFIX = 'blitz-position-';
const EXPIRY_DAYS = 30;
const EXPIRY_MS = EXPIRY_DAYS * 24 * 60 * 60 * 1000;

/**
 * Save playback position for a file
 * @param {string} fileId - File UUID
 * @param {number} queueIndex - Current position in queue
 * @param {number} audioTime - Current time in seconds within the audio
 */
export function savePosition(fileId, queueIndex, audioTime = 0) {
  const key = STORAGE_PREFIX + fileId;
  const data = {
    queueIndex,
    audioTime,
    timestamp: Date.now()
  };

  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    console.warn('Failed to save Blitz position:', e);
  }
}

/**
 * Restore playback position for a file
 * @param {string} fileId - File UUID
 * @param {number} queueLength - Length of the current queue (for validation)
 * @returns {{ queueIndex: number, audioTime: number } | null}
 */
export function restorePosition(fileId, queueLength) {
  const key = STORAGE_PREFIX + fileId;

  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    const data = JSON.parse(stored);

    // Check if expired
    if (Date.now() - data.timestamp > EXPIRY_MS) {
      localStorage.removeItem(key);
      return null;
    }

    // Validate queue index
    if (data.queueIndex < 0 || data.queueIndex >= queueLength) {
      localStorage.removeItem(key);
      return null;
    }

    return {
      queueIndex: data.queueIndex,
      audioTime: data.audioTime || 0
    };
  } catch (e) {
    console.warn('Failed to restore Blitz position:', e);
    return null;
  }
}

/**
 * Clear saved position for a file
 * @param {string} fileId - File UUID
 */
export function clearPosition(fileId) {
  const key = STORAGE_PREFIX + fileId;
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.warn('Failed to clear Blitz position:', e);
  }
}

/**
 * Check if a saved position exists for a file
 * @param {string} fileId - File UUID
 * @returns {boolean}
 */
export function hasPosition(fileId) {
  const key = STORAGE_PREFIX + fileId;
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return false;

    const data = JSON.parse(stored);
    return Date.now() - data.timestamp < EXPIRY_MS;
  } catch (e) {
    return false;
  }
}

